import cn from "classnames";
import { ChangeEvent, FC } from "react";
import styles from "./SubnetCalculator.module.css";
import { getDataAutomationGroup } from "@/helpers";
import { MinusThick, PlusThick } from "@/icons";

interface SubnetBitsInputProps {
  subnetBits: number;
  setSubnetBits: (value: number) => void;
  maskBits: number;
}

export const SubnetBitsInput: FC<SubnetBitsInputProps> = ({
  subnetBits,
  maskBits,
  setSubnetBits,
}) => {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const number = parseInt(e.target.value);
    setSubnetBits(number);
  };

  const handleBlur = () => {
    if (isNaN(subnetBits)) setSubnetBits(0);
    if (subnetBits > 32 - maskBits) {
      setSubnetBits(32 - maskBits);
    }
  };

  const handleIncrement = () => {
    if (subnetBits < 32 - maskBits) {
      setSubnetBits(subnetBits + 1);
    }
  };

  const handleDecrement = () => {
    if (subnetBits > 0) {
      setSubnetBits(subnetBits - 1);
    }
  };

  const handleKeyDownDecrement = (
    e: React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleDecrement();
    }
  };

  const handleKeyDownIncrement = (
    e: React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleIncrement();
    }
  };

  return (
    <div className={cn("relative flex flex-col", styles.calc)}>
      <label
        className="text-body-xs text-swdc-gray-400 pb-2 font-bold"
        htmlFor="subnet-bits"
      >
        Subnet bits
      </label>
      <input
        className="border-swdc-gray-200 text-swdc-gray-400 focus:outline-swdc-black-600 rounded-button h-10 w-full border-2 bg-white px-12 text-center md:w-48"
        id="subnet-bits"
        type="number"
        inputMode="numeric"
        min={0}
        max={32 - maskBits}
        value={subnetBits}
        onChange={handleChange}
        onBlur={handleBlur}
        onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
        {...getDataAutomationGroup("input-field")}
      />
      <button
        className="text-swdc-black-600 hover:text-swdc-gray-300 focus-visible:text-swdc-gray-300 focus-visible:outline-swdc-blue-400 active:text-swdc-black-600 absolute bottom-0 left-5 h-10 px-1.5 focus-visible:outline-2"
        onClick={handleDecrement}
        onKeyDown={handleKeyDownDecrement}
        aria-label="Decrement subnet bits"
      >
        <MinusThick fill="currentColor" width={16} aria-hidden />
      </button>
      <button
        className="text-swdc-black-600 hover:text-swdc-gray-300 focus-visible:text-swdc-gray-300 focus-visible:outline-swdc-blue-400 active:text-swdc-black-600 absolute right-5 bottom-0 h-10 px-1.5 focus-visible:outline-2"
        onClick={handleIncrement}
        onKeyDown={handleKeyDownIncrement}
        aria-label="Increment subnet bits"
      >
        <PlusThick fill="currentColor" width={16} aria-hidden />
      </button>
    </div>
  );
};
